import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Container,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  CircularProgress,
  useTheme,
  alpha
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Star as StarIcon,
  EmojiEvents as TrophyIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import subscriptionService, { SubscriptionPlan, SubscriptionStatus } from '../services/subscriptionService';

interface SubscriptionPlansProps {
  open: boolean;
  onClose: () => void;
  currentSubscription?: SubscriptionStatus;
  onSubscriptionChange?: () => void;
}

const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({
  open,
  onClose,
  currentSubscription,
  onSubscriptionChange
}) => {
  const theme = useTheme();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      fetchPlans();
    }
  }, [open]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const fetchedPlans = await subscriptionService.getPlans();
      setPlans(fetchedPlans);
    } catch (error) {
      console.error('Error fetching plans:', error);
      setError('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planType: string) => {
    try {
      setProcessingPayment(planType);
      setError(null);

      const paymentSuccess = await subscriptionService.processPayment(planType);
      
      if (paymentSuccess) {
        setSuccess(`Successfully upgraded to ${planType === 'monthly' ? 'Monthly' : 'Yearly'} plan!`);
        if (onSubscriptionChange) {
          onSubscriptionChange();
        }
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      if (error.message === 'Payment cancelled by user') {
        setError('Payment was cancelled');
      } else {
        setError('Payment failed. Please try again.');
      }
    } finally {
      setProcessingPayment(null);
    }
  };

  const getPlanIcon = (planId: string) => {
    switch (planId) {
      case 'monthly':
        return <StarIcon sx={{ color: '#3b82f6' }} />;
      case 'yearly':
        return <TrophyIcon sx={{ color: '#f59e0b' }} />;
      default:
        return <CheckIcon sx={{ color: '#10b981' }} />;
    }
  };

  const isPlanActive = (planId: string) => {
    return currentSubscription?.plan === planId && currentSubscription?.isActive;
  };

  const getCurrentPlanBadge = (planId: string) => {
    if (isPlanActive(planId)) {
      return (
        <Chip
          label="Current Plan"
          size="small"
          color="success"
          sx={{ mb: 1 }}
        />
      );
    }
    return null;
  };

  const getPopularBadge = (planId: string) => {
    if (planId === 'yearly') {
      return (
        <Chip
          label="Most Popular"
          size="small"
          sx={{
            bgcolor: '#f59e0b',
            color: 'white',
            mb: 1,
            fontWeight: 'bold'
          }}
        />
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="div" fontWeight="bold">
            Choose Your Plan
          </Typography>
          <Button onClick={onClose} color="inherit">
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Container maxWidth="lg">
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <Typography variant="h6" textAlign="center" color="text.secondary" mb={3}>
            Unlock unlimited access to all subjects and premium features
          </Typography>

          {/* Non-auto-renewal notice */}
          <Box
            sx={{
              p: 2.5,
              mb: 4,
              backgroundColor: alpha(theme.palette.info.main, 0.1),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
              textAlign: 'center'
            }}
          >
            <Typography variant="body1" color="info.dark" sx={{ fontWeight: 600, mb: 1 }}>
              📅 One-time Payment • No Auto-renewal
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              This is a <strong>one-time payment</strong>, not a recurring subscription. 
              Your access will expire after the plan duration and you'll need to <strong>manually renew</strong> once expired. 
              No automatic charges or hidden fees.
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            {plans.map((plan) => (
              <Box key={plan.id} flex={1}>
                <Card
                  elevation={isPlanActive(plan.id) ? 8 : 2}
                  sx={{
                    position: 'relative',
                    height: '100%',
                    border: plan.id === 'yearly' ? `2px solid ${theme.palette.warning.main}` : 
                            isPlanActive(plan.id) ? `2px solid ${theme.palette.success.main}` : 'none',
                    transform: plan.id === 'yearly' ? 'scale(1.05)' : 'none',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: plan.id === 'yearly' ? 'scale(1.08)' : 'scale(1.02)',
                      boxShadow: theme.shadows[8]
                    }
                  }}
                >
                  <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Plan Badge */}
                    <Box mb={2}>
                      {getPopularBadge(plan.id)}
                      {getCurrentPlanBadge(plan.id)}
                    </Box>

                    {/* Plan Header */}
                    <Box display="flex" alignItems="center" mb={2}>
                      {getPlanIcon(plan.id)}
                      <Typography variant="h5" fontWeight="bold" ml={1}>
                        {plan.name}
                      </Typography>
                    </Box>

                    {/* Price */}
                    <Box mb={2}>
                      <Typography variant="h3" fontWeight="bold" color="primary">
                        {subscriptionService.formatPrice(plan.amount)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        per {plan.period}
                      </Typography>
                      {plan.id === 'yearly' && (
                        <Typography variant="body2" color="success.main" fontWeight="bold">
                          {subscriptionService.getSavingsText()}
                        </Typography>
                      )}
                    </Box>

                    {/* Description */}
                    <Typography variant="body1" color="text.secondary" mb={3}>
                      {plan.description}
                    </Typography>

                    {/* Features */}
                    <Box flexGrow={1}>
                      <List dense>
                        {plan.features.map((feature, index) => (
                          <ListItem key={index} sx={{ px: 0 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <CheckIcon color="success" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={feature} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>

                    {/* Action Button */}
                    <Box mt={3}>
                      {isPlanActive(plan.id) ? (
                        <Button
                          fullWidth
                          variant="outlined"
                          color="success"
                          disabled
                          startIcon={<CheckIcon />}
                          sx={{ py: 1.5 }}
                        >
                          Current Plan
                        </Button>
                      ) : (
                        <Button
                          fullWidth
                          variant={plan.id === 'yearly' ? 'contained' : 'outlined'}
                          color={plan.id === 'yearly' ? 'warning' : 'primary'}
                          onClick={() => handleUpgrade(plan.id)}
                          disabled={processingPayment !== null}
                          sx={{
                            py: 1.5,
                            fontWeight: 'bold',
                            ...(processingPayment === plan.id && {
                              position: 'relative'
                            })
                          }}
                        >
                          {processingPayment === plan.id ? (
                            <>
                              <CircularProgress size={20} sx={{ mr: 1 }} />
                              Processing...
                            </>
                          ) : (
                            `Upgrade to ${plan.name}`
                          )}
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Stack>

          {/* Additional Info */}
          <Box mt={4} p={2} bgcolor={alpha(theme.palette.primary.main, 0.05)} borderRadius={1}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              🔒 Secure payments powered by Razorpay • Cancel anytime • No hidden fees
            </Typography>
          </Box>
        </Container>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionPlans; 